import { ComparisonRow, EnrichmentResult, GseaDiseaseEnrichmentDTO, GseaRunningScorePoint } from './types';

const toNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const toStringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === 'string' && value.trim()) {
    return value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }
  return [];
};

export const normalizeRunningScoreTrace = (value: unknown): GseaRunningScorePoint[] => {
  if (!Array.isArray(value)) return [];

  return value.map((item, index) => {
    if (typeof item === 'number') {
      return {
        geneSymbol: `Rank ${index + 1}`,
        rank: index + 1,
        rankFraction: 0,
        inUserGeneList: false,
        runningEnrichmentScore: item,
      };
    }

    const raw = item as Record<string, unknown>;
    return {
      geneSymbol: String(raw.geneSymbol ?? `Rank ${raw.rank ?? index + 1}`),
      ncbiId: raw.ncbiId != null ? toNumber(raw.ncbiId) : undefined,
      disgenetScore: raw.disgenetScore != null ? toNumber(raw.disgenetScore) : undefined,
      rank: toNumber(raw.rank, index + 1),
      rankFraction: toNumber(raw.rankFraction),
      inUserGeneList: Boolean(raw.inUserGeneList),
      runningEnrichmentScore: toNumber(raw.runningEnrichmentScore),
      hitIncrement: raw.hitIncrement != null ? toNumber(raw.hitIncrement) : undefined,
      missPenalty: raw.missPenalty != null ? toNumber(raw.missPenalty) : undefined,
    };
  });
};

export const deriveLeadingEdgeGenes = (
  trace: GseaRunningScorePoint[],
  leadingEdgeSize: number,
  explicitLeadingEdge: string[],
): string[] => {
  if (explicitLeadingEdge.length > 0) return explicitLeadingEdge;
  if (trace.length === 0 || leadingEdgeSize <= 0) return [];

  const peakIndex = trace.reduce((bestIdx, point, idx, arr) => {
    const current = Math.abs(point.runningEnrichmentScore);
    const best = Math.abs(arr[bestIdx].runningEnrichmentScore);
    return current > best ? idx : bestIdx;
  }, 0);

  const leadingUpToPeak = trace
    .slice(0, peakIndex + 1)
    .filter(point => point.inUserGeneList)
    .map(point => point.geneSymbol);

  if (leadingUpToPeak.length > 0) {
    return leadingUpToPeak.slice(0, leadingEdgeSize);
  }

  return trace
    .filter(point => point.inUserGeneList)
    .map(point => point.geneSymbol)
    .slice(0, leadingEdgeSize);
};

export const normalizeGseaResult = (raw: Record<string, unknown>): GseaDiseaseEnrichmentDTO => {
  const diseaseId = String(raw.diseaseId ?? raw.diseaseUMLSCUI ?? raw.diseaseUmlsCui ?? raw.umlsCui ?? '');
  const diseaseName = String(raw.diseaseName ?? raw.name ?? diseaseId);
  const runningScore = normalizeRunningScoreTrace(raw.runningScore);
  const leadingEdgeSize = toNumber(raw.leadingEdgeSize ?? raw.leadingEdgeCount);
  const explicitLeadingEdge = toStringArray(raw.leadingEdge ?? raw.leadingEdgeGenes);

  return {
    diseaseId,
    diseaseName,
    source: raw.source != null ? String(raw.source) : undefined,
    es: toNumber(raw.es ?? raw.enrichmentScore),
    nes: toNumber(raw.nes ?? raw.normalizedEnrichmentScore),
    pvalue: toNumber(raw.pvalue ?? raw.pValue),
    fdr: toNumber(raw.fdr ?? raw.fdrQValue ?? raw.fdrQvalue ?? raw.qvalue),
    matchingGenes: toNumber(
      raw.matchingGenes ?? raw.nuserHits ?? raw.nUserHits ?? raw.nHits ?? raw.hits ?? raw.matchingQueryGenes,
    ),
    geneSetSize: toNumber(
      raw.geneSetSize ?? raw.ntotalDiseaseGenes ?? raw.nTotalDiseaseGenes ?? raw.diseaseGeneSetSize,
    ),
    leadingEdgeSize,
    leadingEdge: deriveLeadingEdgeGenes(runningScore, leadingEdgeSize, explicitLeadingEdge),
    runningScore,
  };
};

const diseaseKey = (id?: string, name?: string): string => {
  const normalizedId = id?.trim();
  if (normalizedId) return normalizedId.toLowerCase();
  return name?.trim().toLowerCase() ?? '';
};

export const buildComparisonRows = (
  fisherResults: EnrichmentResult[] | null,
  gseaResults: GseaDiseaseEnrichmentDTO[] | null,
): ComparisonRow[] => {
  const map = new Map<string, ComparisonRow>();

  (fisherResults ?? []).forEach(item => {
    const key = diseaseKey(item.diseaseUMLSCUI, item.diseaseName);
    if (!key) return;

    map.set(key, {
      diseaseId: item.diseaseUMLSCUI ?? item.diseaseName ?? key,
      diseaseName: item.diseaseName ?? item.diseaseUMLSCUI ?? key,
      fisherPvalue: item.pvalue != null ? toNumber(item.pvalue) : undefined,
      fisherOddsRatio: typeof item.oddsRatio === 'number' ? item.oddsRatio : undefined,
      fisherIntersection: item.intersectionSize != null ? toNumber(item.intersectionSize) : undefined,
      inFisher: true,
      inGsea: false,
    });
  });

  (gseaResults ?? []).forEach(item => {
    const key = diseaseKey(item.diseaseId, item.diseaseName);
    if (!key) return;

    const existing = map.get(key);
    if (existing) {
      map.set(key, {
        ...existing,
        gseaNes: item.nes,
        gseaPvalue: item.pvalue,
        gseaIntersection: item.matchingGenes,
        gseaEs: item.es,
        inGsea: true,
      });
      return;
    }

    map.set(key, {
      diseaseId: item.diseaseId || item.diseaseName,
      diseaseName: item.diseaseName,
      gseaNes: item.nes,
      gseaPvalue: item.pvalue,
      gseaIntersection: item.matchingGenes,
      gseaEs: item.es,
      inFisher: false,
      inGsea: true,
    });
  });

  return [...map.values()].sort((a, b) => {
    const aScore = a.inFisher && a.inGsea ? 0 : a.inFisher || a.inGsea ? 1 : 2;
    const bScore = b.inFisher && b.inGsea ? 0 : b.inFisher || b.inGsea ? 1 : 2;
    if (aScore !== bScore) return aScore - bScore;
    return a.diseaseName.localeCompare(b.diseaseName);
  });
};
